/**
 * Workflow Ordering -- the FI admin's selection surface.
 *
 * Route:  /admin/config/workflow-ordering
 * Scope:  FI-tenant (admin.aaim.com : 9300, adminPortal). The nav group that
 *         carries it is gated on settings:update, which is the same permission
 *         the binding write requires, so the group and the control agree.
 *
 * WHAT A WORKFLOW ORDERING IS. A recipe is the waterfall a pledge runs
 * through: an ordered list of gates -- contract assembled, signing session
 * opened, signature captured, FI acknowledged, FI accepted -- each with the
 * actor who satisfies it, plus the point at which the BINDING control
 * instruction goes to the custodian. Different lenders, custodians, and
 * borrower types need different shapes, which is why the ordering is a
 * selection rather than a constant.
 *
 * WHAT THIS PAGE DOES, and the line it does not cross. Ruling D3 (2026-08-17),
 * reaffirming ruling 3 of 2026-08-13: the FI admin SELECTS one ordering out of
 * a subset an Aaim operator has curated for their tenant. Authoring a recipe,
 * publishing one, and editing the allowlist are Aaim-operator acts on
 * platform.aaim.com : 9303 and are absent here -- not disabled, absent. The
 * server would 403 an FI admin at requirePlatformAdmin, so a control for any
 * of them could only ever be a dead write.
 *
 * THE ONE-OPTION STATE IS THE NORMAL STATE, and this page is built for it.
 * Today exactly one recipe is PUBLISHED platform-wide, so an allowlisted
 * subset will usually hold exactly one entry -- and frequently zero. A chooser
 * rendered over one option implies a decision the tenant does not have, so:
 *
 *   zero options   -- say so plainly, offer the request-change path, render no
 *                     chooser at all
 *   one option, already in effect -- render it read-only as the governing
 *                     ordering, and say that Aaim has made no alternative
 *                     available
 *   selectable alternatives exist -- only then does a Select control appear,
 *                     and only on the options that are not already in effect
 *
 * VALIDATION IS THE SERVER'S. A refused selection returns typed `violations`
 * (V6_BINDING_REQUIRES_PUBLISHED, ALLOWLIST_VIOLATION) which this page renders
 * verbatim. A second copy of those rules in the SPA would be a parallel
 * contract that drifts from the one that actually gates the write.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, BodyText, Button, Card, Heading, type BadgeProps } from '@aaimplatform/ui';
import { ArrowDown, Check, Loader2, Minus, Zap } from 'lucide-react';
import { requestChangeHref } from '../../utils/request-change';
import {
  useWorkflowOrdering,
  type RecipeActor,
  type RecipeGate,
  type RecipeStatus,
  type RecipeValidationError,
  type TenantRecipeBinding,
  type WorkflowRecipe,
} from '../../hooks/useWorkflowOrdering';

/**
 * Exhaustive over the generated unions, so tsc fails if the spec adds a gate
 * or an actor and this file is not updated -- and fails again if a key is
 * added here that the spec does not declare. A hand-listed array typed
 * `RecipeGate[]` would not be checked either way.
 */
const GATE_LABELS: Record<RecipeGate, string> = {
  PLEDGE_CREATED: 'Pledge created',
  CONTRACT_ASSEMBLED: 'Contract assembled',
  ADVISORY_NOTICE_SENT: 'Advisory notice sent',
  SIGNING_SESSION_OPENED: 'Signing session opened',
  SIGNATURE_CAPTURED: 'Signature captured',
  CONTRACT_FULLY_EXECUTED: 'Contract fully executed',
  RETURNED_TO_FI_REVIEW: 'Returned to your staff for review',
  FI_ACKNOWLEDGED: 'Acknowledged by your staff',
  FI_ACCEPTED: 'Accepted by your staff',
  CONTROL_INSTRUMENT_CONFIRMED: 'Control instrument confirmed',
};

const ACTOR_LABELS: Record<RecipeActor, string> = {
  MEMBER: 'Member',
  FI_STAFF: 'Your staff',
  ADVISOR: 'Advisor',
  PLATFORM: 'Aaim',
  CUSTODIAN: 'Custodian',
};

/** Exhaustive over the generated RecipeStatus union, like the maps above. */
const STATUS_LABELS: Record<RecipeStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  RETIRED: 'Retired',
};

function statusTone(status: RecipeStatus): NonNullable<BadgeProps['variant']> {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'DRAFT') return 'warning';
  return 'neutral';
}

/**
 * An instant prints with its year and a zone abbreviation, "Aug 17, 2026,
 * 5:00 AM PDT", the form the Compliance group's audit timestamps use. Without
 * the zone a selection time read by staff in two offices is ambiguous
 * (fi-g4-17).
 */
export function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * The ordering itself, read-only. Position in the list IS the ordering, and
 * the dispatch marker sits after the gate whose completion sends the binding
 * control instruction to the custodian.
 *
 * The gate code (PLEDGE_CREATED ...) is not printed: it is an identifier, and
 * an unbreakable one that ran into the actor label at phone width. It rides
 * on the label's title attribute for anyone reconciling against the spec.
 */
function GateSequence({ recipe }: { recipe: WorkflowRecipe }) {
  return (
    <ol className="space-y-2">
      {recipe.steps.map((step, i) => (
        <li key={step.gate}>
          <div className="flex items-center gap-3 p-3 rounded-sm border border-recessed">
            <span className="font-mono text-xs text-tertiary w-6 shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-sans font-medium break-words" title={step.gate}>
                {GATE_LABELS[step.gate]}
              </div>
            </div>
            <span className="text-xs font-sans text-secondary shrink-0">
              {ACTOR_LABELS[step.actor]}
            </span>
            <span className="flex items-center gap-1 text-xs font-sans text-tertiary shrink-0">
              {step.required ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
              {step.required ? 'Required' : 'Optional'}
            </span>
          </div>

          {recipe.bindingDispatchAfter === step.gate && (
            <div
              data-testid="binding-dispatch-marker"
              className="flex items-center gap-2 pl-9 py-1 text-xs font-sans text-secondary"
            >
              <Zap className="w-3 h-3 text-tertiary" aria-hidden="true" />
              Control instrument (binding) dispatches to the custodian here
            </div>
          )}
          {i < recipe.steps.length - 1 && (
            <div className="pl-9 py-0.5 text-tertiary">
              <ArrowDown className="w-3 h-3" />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function RecipeIdentity({ recipe }: { recipe: WorkflowRecipe }) {
  return (
    <div className="flex items-start justify-between gap-4">
      {/* The recipe slug and version identify the ordering to Aaim, not to the
          reader; they ride on the title attribute instead of printing. */}
      <div title={`${recipe.recipeId} v${recipe.version}`}>
        <Heading level={3} size="card">
          {recipe.title}
        </Heading>
      </div>
      <Badge variant={statusTone(recipe.status)} size="small">
        {STATUS_LABELS[recipe.status]}
      </Badge>
    </div>
  );
}

function ViolationList({
  violations,
  message,
}: {
  violations: RecipeValidationError[];
  message: string;
}) {
  return (
    <Alert type="error" title="Aaim refused this selection">
      <BodyText variant="ui" size="sm">
        {message}
      </BodyText>
      {violations.length > 0 && (
        <ul className="mt-2 space-y-1">
          {violations.map((v, i) => (
            <li key={`${v.code}-${i}`} className="text-sm font-sans">
              <span className="font-mono text-xs mr-2">{v.code}</span>
              {v.message}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

/**
 * The consumer-mirror parity signal (GH #2580). The binding is the authority
 * for which ordering this institution has selected; the mirror is the copy the
 * member-facing stack reads when it pins a new pledge, and they are joined by
 * a push that can fail. While they disagree, every new pledge runs the
 * superseded ordering -- so a page that showed only the authority would show
 * an ordering that is not in effect.
 *
 * Rendered only when the server reports the mirror out of sync. An ABSENT
 * mirror means no sweep has completed and the state is unknown; unknown is
 * never reported as healthy, and it is never reported as broken either.
 */
function MirrorNotice({ binding }: { binding: TenantRecipeBinding }) {
  if (!binding.mirror || binding.mirror.inSync) return null;
  return (
    <Alert type="warning" title="This selection is not yet in effect for new pledges">
      <BodyText variant="ui" size="sm">
        Aaim last checked at {formatTimestamp(binding.mirror.checkedAt)} and the member-facing
        stack had not yet picked up this ordering. Pledges created in the meantime run the
        previous ordering. Aaim retries automatically; if this persists, request a change below.
      </BodyText>
    </Alert>
  );
}

export function WorkflowOrderingPage() {
  const navigate = useNavigate();
  const { binding, selectedRecipe, options, isLoading, loadError, select } = useWorkflowOrdering();
  const [failure, setFailure] = useState<{
    violations: RecipeValidationError[];
    message: string;
  } | null>(null);

  const isInEffect = (recipe: WorkflowRecipe): boolean =>
    binding?.recipeId === recipe.recipeId && binding?.recipeVersion === recipe.version;

  const alternatives = options.filter((r) => !isInEffect(r));

  const handleSelect = (recipe: WorkflowRecipe) => {
    setFailure(null);
    select.mutate(recipe, {
      onSuccess: (result) => {
        if (!result.ok) setFailure({ violations: result.violations, message: result.message });
      },
      onError: (err: Error) => setFailure({ violations: [], message: err.message }),
    });
  };

  const handleRequestChange = () => {
    // `void` because react-router's navigate returns a promise this handler has
    // nothing to do with (@typescript-eslint/no-floating-promises).
    void navigate(
      requestChangeHref({
        surface: 'Workflow ordering',
        item: 'A different pledge workflow ordering',
        detail: binding
          ? `Currently selected: ${binding.recipeId} v${binding.recipeVersion}`
          : 'No ordering selected; your institution runs the Aaim platform default.',
      }),
    );
  };

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant heading. */}
        <p className="text-sm font-sans text-tertiary leading-normal max-w-3xl">
          The order your members&apos; pledges move through: when the contract is assembled,
          when signatures are gathered, when your staff acknowledges and accepts, and when the
          binding control instruction reaches the custodian. Aaim curates the orderings; your
          institution selects which one governs. Selecting one applies to pledges created after
          the change, and a pledge already in flight keeps the ordering it started under.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-secondary">
          <Loader2 className="w-4 h-4 animate-spin" />
          <BodyText variant="ui" size="sm">
            Loading your workflow ordering...
          </BodyText>
        </div>
      ) : loadError ? (
        <Alert type="error" title="Could not load workflow ordering">
          <BodyText variant="ui" size="sm">
            {loadError.message}
          </BodyText>
        </Alert>
      ) : (
        <div className="space-y-6">
          {failure && <ViolationList violations={failure.violations} message={failure.message} />}
          {binding && <MirrorNotice binding={binding} />}

          {/* ---- In effect now ------------------------------------------ */}
          <Card className="p-6">
            <div className="space-y-4">
              {/* Section headings sit under AppHeader's h1, so they are h2
                  styled as card titles; the recipe title under a section stays
                  h3 (fi-g4-15). */}
              <Heading level={2} as={3} size="card">
                In effect now
              </Heading>

              {!binding ? (
                <BodyText variant="ui" size="sm" color="secondary">
                  Your institution has not selected an ordering, so pledges run the Aaim platform
                  default. Aaim sets that default; this page does not. Selecting one of the
                  orderings below pins your institution to it explicitly.
                </BodyText>
              ) : (
                <>
                  <BodyText variant="ui" size="sm" color="secondary">
                    Selected by {binding.selectedBy} on {formatTimestamp(binding.effectiveFrom)}.
                  </BodyText>
                  {selectedRecipe ? (
                    <>
                      <RecipeIdentity recipe={selectedRecipe} />
                      <GateSequence recipe={selectedRecipe} />
                    </>
                  ) : (
                    <BodyText variant="ui" size="sm" color="secondary">
                      Your institution is pinned to{' '}
                      <span className="font-mono text-xs">
                        {binding.recipeId} v{binding.recipeVersion}
                      </span>
                      , and Aaim could not load its steps. The selection still governs your
                      pledges. Request a change below if this persists.
                    </BodyText>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* ---- What Aaim has made available --------------------------- */}
          <Card className="p-6">
            <div className="space-y-4">
              <Heading level={2} as={3} size="card">
                Available to your institution
              </Heading>

              {options.length === 0 ? (
                <>
                  <BodyText variant="ui" size="sm" color="secondary">
                    Aaim has not made any ordering available for your institution to select. There
                    is nothing to choose here yet, and your pledges continue to run the ordering
                    described above.
                  </BodyText>
                  <Button variant="secondary" size="small" onClick={handleRequestChange}>
                    Request a different ordering
                  </Button>
                </>
              ) : (
                <>
                  <BodyText variant="ui" size="sm" color="secondary">
                    {alternatives.length === 0
                      ? 'This is the only ordering Aaim has made available to your institution. Aaim curates the catalog; use the request below to ask for another.'
                      : 'Select the ordering your institution should run. Each one shows the gates it enforces, who satisfies each gate, and where the binding control instruction dispatches.'}
                  </BodyText>

                  <div className="space-y-4">
                    {options.map((recipe) => {
                      const inEffect = isInEffect(recipe);
                      // The in-effect ordering's steps already render in the
                      // card above; repeating all of them here doubled the page
                      // in the normal one-option state. They repeat only when
                      // the card above could not load them.
                      const showSteps = !inEffect || !selectedRecipe;
                      return (
                        <div
                          key={`${recipe.recipeId}-${recipe.version}`}
                          className={`p-4 rounded-sm border border-recessed ${
                            inEffect ? 'bg-elevated' : ''
                          }`}
                        >
                          <div className="space-y-4">
                            <RecipeIdentity recipe={recipe} />
                            {showSteps && <GateSequence recipe={recipe} />}
                            <div className="flex items-center gap-3">
                              {inEffect ? (
                                <BodyText variant="ui" size="sm" color="secondary">
                                  In effect for your institution.
                                  {selectedRecipe ? ' Its steps are shown above.' : ''}
                                </BodyText>
                              ) : (
                                <Button
                                  variant="primary"
                                  size="small"
                                  disabled={select.isPending}
                                  onClick={() => handleSelect(recipe)}
                                >
                                  {select.isPending ? 'Selecting...' : 'Select this ordering'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button variant="secondary" size="small" onClick={handleRequestChange}>
                    Request a different ordering
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
